import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Profile {
  id: string;
  Email: string;
  Role: string;
  Status: string;
  created_at: string;
}

export default function ProfilesTab() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('Profiles')
        .select('id, Email, Role, Status, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setProfiles(data || []);
    } catch (err: any) {
      console.error('Error al cargar perfiles:', err);
      setError(err.message || 'Error al cargar perfiles desde la base de datos');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = profiles.filter(p =>
    (p.Email || '').toLowerCase().includes(search.toLowerCase())
  );

  const getRoleColor = (role: string) => {
    if (!role) return 'bg-gray-100 text-gray-500';
    const r = role.toLowerCase();
    if (r === 'admin') return 'bg-rose-100 text-rose-700';
    if (r === 'empresa') return 'bg-amber-100 text-amber-700';
    if (r === 'candidato') return 'bg-emerald-100 text-emerald-700';
    return 'bg-gray-100 text-gray-600';
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-500';
    const s = status.toLowerCase();
    if (s === 'active' || s === 'activo') return 'bg-emerald-100 text-emerald-700';
    if (s === 'pending' || s === 'pendiente') return 'bg-amber-100 text-amber-700';
    if (s === 'inactive' || s === 'inactivo') return 'bg-gray-100 text-gray-500';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div>
      {/* Buscador */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        {error && (
          <div className="m-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
            <div>
              <p className="text-sm font-medium text-red-800">Error al cargar usuarios</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
              <button
                onClick={loadProfiles}
                className="mt-2 text-sm text-red-700 underline hover:text-red-800 cursor-pointer"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="text-center py-16">
            <i className="ri-loader-4-line text-4xl text-gray-300 animate-spin block mb-2"></i>
            <p className="text-gray-500 text-sm">Cargando usuarios...</p>
          </div>
        ) : filtered.length === 0 && !error ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-line text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-900 font-medium mb-1">Sin usuarios registrados</p>
            <p className="text-gray-500 text-sm">No se encontraron perfiles en la base de datos</p>
          </div>
        ) : !error && (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registro</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(profile => (
                <tr key={profile.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-sm text-gray-500"></i>
                      </div>
                      <span className="text-sm text-gray-900">{profile.Email || '—'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getRoleColor(profile.Role)}`}>
                      {profile.Role || 'Sin rol'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(profile.Status)}`}>
                      {profile.Status || 'Sin estado'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500">
                      {profile.created_at
                        ? new Date(profile.created_at).toLocaleDateString('es-ES', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })
                        : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer con total */}
      {!loading && !error && filtered.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-500">{filtered.length} usuario{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}</p>
        </div>
      )}
    </div>
  );
}